import moment from 'moment';
import { flatten, uniq } from 'lodash';

import type { GraphOverTimeSOV, LineGraphDateInterval, TimeframeItem, TimeframeResult } from 'types';

type SovExtractedData = {
  datesFlattened: Array<string>,
  totals: Array<Array<number>>,
  yAxisMin: number,
  yAxisMax: number,
  yAxisValues: Array<Array<number>>
};

export const GRAPH_COLORS = [
  '#00D09E',
  '#4DC2EE',
  '#4F78C2',
  '#BE8FD7',
  '#E2629C',
  '#FF845A',
  '#F2C400',
  '#AECC67',
  '#22866B',
  '#026489',
  '#33277D',
  '#B82F6B',
  '#FCA4A9',
  '#C89239',
  '#FFE64C'
];

export function getGraphDateInterval(startDate: string | null, endDate: string | null): LineGraphDateInterval {
  const mStartDate = moment(startDate || '');
  const mEndDate = moment(endDate || '');
  const daysDiff = mEndDate.diff(mStartDate, 'days');

  if (daysDiff < 30) {
    return 'day';
  }

  if (daysDiff === 30 || daysDiff < 90) {
    return 'week';
  }

  return 'month';
}

export function calculateStartEndDates(
  timeframeItems: Array<TimeframeItem>,
  timeframeId: string,
  startDate?: string
): TimeframeResult {
  const timeframe = timeframeItems.find(item => item.id === timeframeId);
  const id = timeframe && timeframe.id;
  const value = timeframe && timeframe.value;

  let updatedStartDate;

  // all graphs
  if (value && value !== -1) {
    updatedStartDate = moment().subtract(value, 'days');
  }

  // influencer profile graphs
  if (id === 'all-time') {
    updatedStartDate = moment(moment().unix());
  }

  // sov graphs
  if (id === 'custom') {
    updatedStartDate = startDate ? moment(startDate) : '';
  }

  return {
    startDate: updatedStartDate ? updatedStartDate.format('YYYY-MM-DD') : '',
    endDate: updatedStartDate ? moment().format('YYYY-MM-DD') : ''
  };
}

function extractData(data: any, key: string, prop: string): Array<any> {
  return data.map(entry => (
    (entry && entry[key] || []).map(item => item[prop])
  ));
}

function getYAxisPadValue(type: string, minValue: number, maxValue: number): number {
  if (type === 'min') {
    return minValue - (maxValue / 10);
  }

  return maxValue + (maxValue / 10);
}

function getYAxisPadPercentage(type: string, value: number): number {
  const pad = 10;
  const diff = type === 'min' ? value - pad : value + pad;

  if (type === 'min') {
    return diff < 0 ? -10 : diff;
  }

  return diff > 100 ? 110 : diff;
}

export function extractSOVGraphData(data: Array<GraphOverTimeSOV>, key: string, isSingleQuery: boolean): SovExtractedData {
  const percentages = extractData(data, key, 'percentage');
  const percentagesFlattened = flatten(percentages);
  const dates = extractData(data, key, 'date');
  const datesFlattened = uniq(flatten(dates));
  const totals = extractData(data, key, 'count');
  const totalsFlattened = flatten(totals);

  const yAxisMin = isSingleQuery
    ? getYAxisPadValue('min', Math.min(...totalsFlattened), Math.max(...totalsFlattened))
    : getYAxisPadPercentage('min', Math.min(...percentagesFlattened));

  const yAxisMax = isSingleQuery
    ? getYAxisPadValue('max', Math.min(...totalsFlattened), Math.max(...totalsFlattened))
    : getYAxisPadPercentage('max', Math.max(...percentagesFlattened));

  return {
    // $FlowIgnore - Cannot return object literal because number [1] is incompatible with string
    datesFlattened,
    totals,
    yAxisMin,
    yAxisMax,
    yAxisValues: isSingleQuery ? totals : percentages
  };
}
